/**
 * Session loss (imsight#106). The proxy answers a gone session with
 * 401 {code: SESSION_ENDED, kind, reason}; the app shows a signed-out dialog
 * instead of redirecting to login.
 */

import { GatewayError, GatewayErrorCode } from './gateway/error-codes';
import { logger } from './logger';

/** signed_out: the person logged out in another tab (#110). */
export type SessionEndKind = 'expired' | 'access_ended' | 'signed_out';

export interface SessionEndInfo {
  kind: SessionEndKind;
  /** Backend reason (NO_SESSION, MAX_AGE, …) or UNKNOWN; for logs only. */
  reason: string;
  /** Reported by another tab (#110): handled here, not passed on again. */
  fromOtherTab?: boolean;
}

export class SessionEndedError extends GatewayError {
  constructor(public readonly info: SessionEndInfo) {
    super(
      `Authentication required: session ended (${info.reason})`,
      GatewayErrorCode.SESSION_ENDED,
      { status: 401, kind: info.kind, reason: info.reason }
    );
    this.name = 'SessionEndedError';
  }
}

type SessionEndedListener = (info: SessionEndInfo) => void;

let listener: SessionEndedListener | null = null;
let endedWith: SessionEndInfo | null = null;

/** The app registers the handler that stops polling and shows the dialog. */
export function setSessionEndedListener(
  next: SessionEndedListener | null
): void {
  listener = next;
}

/**
 * Report a session loss. Returns true when the app handles it (a listener is
 * registered); the listener runs only for the first report.
 */
export function notifySessionEnded(info: SessionEndInfo): boolean {
  if (!listener) return false;
  if (endedWith) return true;
  endedWith = info;
  try {
    listener(info);
  } catch (error) {
    // Never throw into request callers; the app's listener has its own fallback.
    logger.error('Session-ended listener failed:', error);
  }
  return true;
}

export function hasSessionEnded(): boolean {
  return endedWith !== null;
}

/** A promise that never settles: the dialog owns the screen, callers show nothing. */
function never<T>(): Promise<T> {
  return new Promise<T>(() => {});
}

/** For catch blocks: suspend the caller if this error ended a handled session. */
export function suspendIfSessionEnded<T>(error: unknown): Promise<T> | null {
  if (error instanceof SessionEndedError && notifySessionEnded(error.info)) {
    return never<T>();
  }
  return null;
}

/** For request starts: don't fire requests (or CSRF errors) after the session ended. */
export function pendingIfSessionEnded<T>(): Promise<T> | null {
  return endedWith && listener ? never<T>() : null;
}

/** Test helper. */
export function resetSessionEndedState(): void {
  listener = null;
  endedWith = null;
}
